
// Query-key and permission helpers for the vendor 2FA badges. Pure, so they
// are tested directly — this monorepo has no jsdom, so behaviour that matters
// lives here rather than inside a component (spec §8).

export const MFA_STATUS_KEY = 'company-mfa-status';

/**
 * Cache key for a page of vendor ids.
 *
 * Sorted and de-duped because the Vendors list re-sorts on every header click
 * while showing the SAME vendors: keyed on the raw array, each re-sort would
 * be a cache miss and refetch a page of statuses that had not changed. The
 * shared prefix is what lets a reset invalidate every status query at once.
 */
export function statusQueryKey(ids: string[]): [string, string[]] {
  return [MFA_STATUS_KEY, [...new Set(ids)].sort()];
}

/**
 * Whether to offer "Reset 2FA" for this vendor to this admin.
 *
 * Super admins only: reset plus access to the vendor's mailbox is effectively
 * full access to their portal, which is why the server gates the route with
 * requireSuperAdminRole. This mirrors that rule so the button is not shown to
 * someone the API would refuse.
 *
 * Hidden only when we KNOW there is nothing to reset (`enrolled === false`).
 * An UNKNOWN status still shows it, deliberately: status is undefined exactly
 * when 0088 is unapplied or the RPC is failing, which is when support most
 * needs the recovery tool. Hiding it there removes the button during the
 * outage it exists for.
 *
 * "Fail closed" belongs on the server, not on an affordance — the route still
 * enforces requireSuperAdminRole, and a reset against no factors is a harmless
 * no-op that reports removed:0.
 */
export function canResetVendorMfa(
  // A plain string, not AdminUser['role']: this value arrives off the wire, so
  // the type system cannot promise it is one of the three tiers. A token
  // minted before migration 0090 still carries 'admin', and the allow-list
  // below is what puts it on the floor rather than above it.
  role: string | undefined,
  enrolled: boolean | undefined,
): boolean {
  if (enrolled === false) return false;
  return role === 'super_admin';
}
